import { useCallback, useEffect, useRef, useState, type TouchList } from "react";
import { Minus, Plus, RotateCcw } from "lucide-react";
import { cn } from "../lib/utils";
import { Button } from "./button";
import { Dialog, DialogContent, DialogTitle } from "./dialog";

const MIN_SCALE = 1;
const MAX_SCALE = 4;

const clampScale = (value: number) =>
  Math.min(MAX_SCALE, Math.max(MIN_SCALE, value));

const touchDistance = (touches: TouchList) => {
  if (touches.length < 2) return 0;
  const dx = touches[0]!.clientX - touches[1]!.clientX;
  const dy = touches[0]!.clientY - touches[1]!.clientY;
  return Math.hypot(dx, dy);
};

export type ImageZoomDialogProps = {
  src: string;
  alt: string;
  open: boolean;
  onOpenChange: (open: boolean) => void;
};

export const ImageZoomDialog = ({
  src,
  alt,
  open,
  onOpenChange,
}: ImageZoomDialogProps) => {
  const [scale, setScale] = useState(1);
  const [offset, setOffset] = useState({ x: 0, y: 0 });
  const dragRef = useRef<{
    pointerId: number;
    x: number;
    y: number;
    ox: number;
    oy: number;
  } | null>(null);
  const pinchRef = useRef<{ distance: number; scale: number } | null>(null);

  const resetView = useCallback(() => {
    setScale(1);
    setOffset({ x: 0, y: 0 });
    dragRef.current = null;
    pinchRef.current = null;
  }, []);

  useEffect(() => {
    if (!open) resetView();
  }, [open, resetView]);

  const zoomBy = (delta: number) => {
    setScale((current) => clampScale(Number((current + delta).toFixed(2))));
  };

  const onWheel = (event: React.WheelEvent<HTMLDivElement>) => {
    event.preventDefault();
    zoomBy(event.deltaY < 0 ? 0.2 : -0.2);
  };

  const onPointerDown = (event: React.PointerEvent<HTMLDivElement>) => {
    if (scale <= 1 || event.pointerType === "touch") return;
    event.currentTarget.setPointerCapture(event.pointerId);
    dragRef.current = {
      pointerId: event.pointerId,
      x: event.clientX,
      y: event.clientY,
      ox: offset.x,
      oy: offset.y,
    };
  };

  const onPointerMove = (event: React.PointerEvent<HTMLDivElement>) => {
    if (!dragRef.current || dragRef.current.pointerId !== event.pointerId) return;
    setOffset({
      x: dragRef.current.ox + event.clientX - dragRef.current.x,
      y: dragRef.current.oy + event.clientY - dragRef.current.y,
    });
  };

  const onPointerUp = (event: React.PointerEvent<HTMLDivElement>) => {
    if (dragRef.current?.pointerId === event.pointerId) {
      dragRef.current = null;
    }
  };

  const onTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length === 2) {
      pinchRef.current = {
        distance: touchDistance(event.touches),
        scale,
      };
    }
  };

  const onTouchMove = (event: React.TouchEvent<HTMLDivElement>) => {
    if (event.touches.length !== 2 || !pinchRef.current) return;
    const distance = touchDistance(event.touches);
    if (distance <= 0 || pinchRef.current.distance <= 0) return;
    const next = pinchRef.current.scale * (distance / pinchRef.current.distance);
    setScale(clampScale(Number(next.toFixed(2))));
    event.preventDefault();
  };

  const onTouchEnd = () => {
    pinchRef.current = null;
  };

  const onDoubleClick = () => {
    setScale((current) => {
      if (current > 1) {
        setOffset({ x: 0, y: 0 });
        return 1;
      }
      return 2;
    });
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent
        aria-describedby={undefined}
        className="top-[max(0.5rem,env(safe-area-inset-top))] flex h-[min(94dvh,960px)] w-[min(98vw,1200px)] max-w-none translate-y-0 flex-col border-0 bg-zinc-950 p-0 text-white sm:top-1/2 sm:-translate-y-1/2"
      >
        <DialogTitle className="sr-only">{alt}</DialogTitle>
        <div
          className={cn(
            "relative min-h-0 flex-1 touch-none overflow-hidden",
            scale > 1 ? "cursor-grab active:cursor-grabbing" : "cursor-zoom-in",
          )}
          onWheel={onWheel}
          onPointerDown={onPointerDown}
          onPointerMove={onPointerMove}
          onPointerUp={onPointerUp}
          onPointerCancel={onPointerUp}
          onTouchStart={onTouchStart}
          onTouchMove={onTouchMove}
          onTouchEnd={onTouchEnd}
          onDoubleClick={onDoubleClick}
        >
          <div className="flex h-full w-full items-center justify-center">
            <img
              src={src}
              alt={alt}
              draggable={false}
              className="max-h-full max-w-full select-none object-contain transition-transform duration-75"
              style={{
                transform: `translate(${offset.x}px, ${offset.y}px) scale(${scale})`,
              }}
            />
          </div>
        </div>

        <div className="border-white/10 flex shrink-0 items-center justify-center gap-2 border-t px-4 py-3">
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="bg-white/10 text-white hover:bg-white/20"
            onClick={() => zoomBy(-0.25)}
            disabled={scale <= MIN_SCALE}
            aria-label="Dézoomer"
          >
            <Minus className="h-4 w-4" />
          </Button>
          <span className="min-w-12 text-center text-xs tabular-nums text-white/80">
            {Math.round(scale * 100)}%
          </span>
          <Button
            type="button"
            size="icon"
            variant="secondary"
            className="bg-white/10 text-white hover:bg-white/20"
            onClick={() => zoomBy(0.25)}
            disabled={scale >= MAX_SCALE}
            aria-label="Zoomer"
          >
            <Plus className="h-4 w-4" />
          </Button>
          <Button
            type="button"
            size="sm"
            variant="secondary"
            className="bg-white/10 text-white hover:bg-white/20"
            onClick={resetView}
            disabled={scale === 1 && offset.x === 0 && offset.y === 0}
          >
            <RotateCcw className="mr-1.5 h-4 w-4" />
            Réinitialiser
          </Button>
        </div>
      </DialogContent>
    </Dialog>
  );
};
